import type { NavigateFunction } from 'react-router-dom';
import { getStudioAppUrl } from '../config/studio';

const LOGIN_RETURN_KEY = 'loginReturnUrl';

function getAllowedReturnOrigins(): string[] {
  const origins = new Set<string>([window.location.origin]);
  const studio = getStudioAppUrl();
  try {
    origins.add(new URL(studio).origin);
  } catch {
    // ignore invalid VITE_STUDIO_URL
  }
  if (import.meta.env.DEV) {
    origins.add('http://localhost:5173');
  }
  return [...origins];
}

/** Reject open redirects; allow in-app paths and trusted product origins (Studio). */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('/')) {
    if (trimmed.startsWith('//')) return null;
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    if (getAllowedReturnOrigins().includes(url.origin)) return url.href;
  } catch {
    return null;
  }
  return null;
}

export function storeLoginReturnUrl(url: string | null | undefined): void {
  const safe = sanitizeReturnTo(url);
  if (!safe || safe === '/' || safe === '/auth-callback') return;
  sessionStorage.setItem(LOGIN_RETURN_KEY, safe);
}

export function consumeLoginReturnUrl(): string | null {
  const returnUrl = sessionStorage.getItem(LOGIN_RETURN_KEY);
  sessionStorage.removeItem(LOGIN_RETURN_KEY);
  return sanitizeReturnTo(returnUrl);
}

export function followLoginReturnUrl(returnUrl: string | null, navigate: NavigateFunction): void {
  if (!returnUrl || returnUrl === '/' || returnUrl === '/auth-callback') return;

  if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
    window.location.assign(returnUrl);
    return;
  }

  navigate(returnUrl, { replace: true });
}
